// components/CheckoutDialog.jsx
import React, { useState, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  Grid,
  Typography,
  TextField,
  IconButton,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import QRCode from 'qrcode';
import { useCart } from '../context/CartContext';
import axios from 'axios';

const CheckoutDialog = ({ open, onClose, locationHashId }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const {
    cart,
    cartItems,
    total,
    orderNotes,
    setOrderNotes,
    updateCartQuantity,
    handleProductNotesChange,
    clearCart,
  } = useCart();

  const [paymentStep, setPaymentStep] = useState('cart');
  const [qrPaymentData, setQrPaymentData] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const canvasRef = useRef(null);

  const handlePlaceOrder = async () => {
    if (cartItems.length === 0) {
      setSnackbar({
        open: true,
        message: intl.formatMessage({
          id: 'app.cartEmpty',
          defaultMessage: 'El carrito está vacío.',
        }),
        severity: 'warning',
      });
      return;
    }

    const orderItems = cartItems.map((item) => ({
      productId: item.product.id,
      quantity: item.quantity,
      unitPrice: parseFloat(item.product.price),
      productNotes: item.productNotes,
    }));

    try {
      const orderResponse = await axios.post('/api/orders', {
        locationHashId: locationHashId,
        items: orderItems,
        notes: orderNotes,
      });

      if (orderResponse.data && orderResponse.data.order) {
        const createdOrderId = orderResponse.data.order.id;
        setOrderId(createdOrderId);

        const paymentResponse = await axios.post('/api/payment/create-qr-payment', {
          gateway: 'mercadopago',
          amount: parseFloat(total) * 100,
          description: `Pedido #${createdOrderId}`,
          customerId: createdOrderId.toString(),
          customerEmail: 'customer@email.com',
        });

        if (paymentResponse.data.success) {
          console.log('QR Payment Data:', paymentResponse.data);
          setQrPaymentData(paymentResponse.data);
          setPaymentStep('payment');
        }
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: intl.formatMessage(
          {
            id: 'app.orderFailed',
            defaultMessage: 'Error al realizar el pedido: {error}',
          },
          { error: error.response?.data?.message || error.message }
        ),
        severity: 'error',
      });
    }
  };

  useEffect(() => {
    if (qrPaymentData?.qrData && canvasRef.current) {
      QRCode.toCanvas(canvasRef.current, qrPaymentData.qrData, { width: 256 });
    }
  }, [qrPaymentData]);

  useEffect(() => {
    if (!qrPaymentData?.orderId || paymentStep !== 'payment') return;

    const checkPayment = async () => {
      try {
        const response = await axios.get(
          `/api/payment/verify-qr-payment/mercadopago/${qrPaymentData.orderId}`
        );

        if (response.data.status === 'closed' || response.data.status === 'paid') {
          setPaymentStep('success');
          clearCart();

          setTimeout(() => {
            onClose();
            navigate(`/order-confirmation/${orderId}`);
          }, 3000);
        }
      } catch (err) {
        console.error('Error verificando pago:', err);
      }
    };

    const interval = setInterval(checkPayment, 3000);
    return () => clearInterval(interval);
  }, [qrPaymentData, paymentStep]);

  const handleClose = () => {
    if (paymentStep !== 'payment') {
      setPaymentStep('cart');
      setQrPaymentData(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <FormattedMessage id="app.yourCart" defaultMessage="Tu Carrito" />
      </DialogTitle>
      <DialogContent dividers>
        {paymentStep === 'cart' && (
          <>
            {cartItems.length === 0 ? (
              <Typography>
                <FormattedMessage id="app.cartIsEmpty" defaultMessage="El carrito está vacío." />
              </Typography>
            ) : (
              <List>
                {cartItems.map((item) => (
                  <ListItem key={item.product.id} divider>
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body1" fontWeight="bold">
                          {item.product.name}
                        </Typography>
                        <TextField
                          label={
                            <FormattedMessage
                              id="app.productNotes"
                              defaultMessage="Notas para el producto"
                            />
                          }
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={item.productNotes}
                          onChange={(e) =>
                            handleProductNotesChange(item.product.id, e.target.value)
                          }
                          sx={{ mt: 1 }}
                        />
                      </Grid>
                      <Grid item xs={3} sx={{ textAlign: 'right' }}>
                        <Typography variant="body2">
                          ${parseFloat(item.product.price).toFixed(2)} c/u
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          Total: ${(item.product.price * item.quantity).toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Box display="flex" alignItems="center" justifyContent="flex-end">
                          <IconButton
                            size="small"
                            onClick={() => updateCartQuantity(item.product.id, item.quantity - 1)}
                          >
                            <RemoveIcon />
                          </IconButton>
                          <Typography mx={1}>{item.quantity}</Typography>
                          <IconButton
                            size="small"
                            onClick={() => updateCartQuantity(item.product.id, item.quantity + 1)}
                          >
                            <AddIcon />
                          </IconButton>
                        </Box>
                      </Grid>
                    </Grid>
                  </ListItem>
                ))}
              </List>
            )}
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderTop: '1px solid #eee',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6">
                <FormattedMessage id="app.cartTotal" defaultMessage="Total del Carrito:" />
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                ${total}
              </Typography>
            </Box>
            <TextField
              label={
                <FormattedMessage
                  id="app.orderGeneralNotes"
                  defaultMessage="Notas generales para el pedido"
                />
              }
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              value={orderNotes}
              onChange={(e) => setOrderNotes(e.target.value)}
              sx={{ mt: 2 }}
            />
          </>
        )}

        {paymentStep === 'payment' && (
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <Typography variant="h5" gutterBottom>
              <FormattedMessage
                id="app.scanQRToPay"
                defaultMessage="Escaneá el QR para pagar"
              />
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Total: ${total}
            </Typography>

            <Box sx={{ display: 'inline-block', p: 3, bgcolor: 'white', borderRadius: 2 }}>
              <canvas ref={canvasRef} />
            </Box>

            <Box sx={{ mt: 3 }}>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                <FormattedMessage
                  id="app.waitingForPayment"
                  defaultMessage="Esperando pago..."
                />
              </Typography>
            </Box>
          </Box>
        )}

        {paymentStep === 'success' && (
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <Alert severity="success">
              <Typography variant="h6">
                <FormattedMessage id="app.paymentSuccessful" defaultMessage="¡Pago exitoso!" />
              </Typography>
              <Typography>
                <FormattedMessage
                  id="app.redirectingToOrder"
                  defaultMessage="Redirigiendo a tu pedido..."
                />
              </Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {paymentStep === 'cart' && (
          <>
            <Button onClick={handleClose} color="primary">
              <FormattedMessage id="app.continueShopping" defaultMessage="Seguir Comprando" />
            </Button>
            <Button
              onClick={handlePlaceOrder}
              color="secondary"
              variant="contained"
              disabled={cartItems.length === 0}
            >
              <FormattedMessage id="app.placeOrder" defaultMessage="Hacer Pedido" />
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CheckoutDialog;